import * as React from 'react'
import { Tooltip as TooltipPrimitive } from 'radix-ui'

import { cn } from '@/shared/lib/utils'

const TooltipProvider = ({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) => (
  <TooltipPrimitive.Provider
    data-slot="tooltip-provider"
    delayDuration={delayDuration}
    {...props}
  />
)

/** Root without a nested Provider — use inside a shared TooltipProvider. */
const TooltipRoot = ({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) => (
  <TooltipPrimitive.Root data-slot="tooltip" {...props} />
)

const Tooltip = ({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) => (
  <TooltipProvider>
    <TooltipRoot {...props} />
  </TooltipProvider>
)

const TooltipTrigger = React.forwardRef<
  React.ComponentRef<typeof TooltipPrimitive.Trigger>,
  React.ComponentProps<typeof TooltipPrimitive.Trigger>
>(({ onPointerDown, onClick, ...props }, ref) => {
  const handlePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.pointerType === 'touch') {
      event.preventDefault()
    }
    onPointerDown?.(event)
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(event)
  }

  return (
    <TooltipPrimitive.Trigger
      ref={ref}
      data-slot="tooltip-trigger"
      onPointerDown={handlePointerDown}
      onClick={handleClick}
      {...props}
    />
  )
})
TooltipTrigger.displayName = 'TooltipTrigger'

const TooltipContent = ({
  className,
  sideOffset = 4,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      data-slot="tooltip-content"
      sideOffset={sideOffset}
      className={cn(
        'bg-primary text-primary-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 z-50 w-fit max-w-xs rounded-md px-3 py-1.5 text-xs text-balance',
        className,
      )}
      {...props}
    >
      {children}
      <TooltipPrimitive.Arrow className="bg-primary fill-primary z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]" />
    </TooltipPrimitive.Content>
  </TooltipPrimitive.Portal>
)

/**
 * Touch-friendly Tooltip that opens on tap (mobile) and hover (desktop).
 * Wraps Radix Tooltip.Root with controlled open state that responds to touch events.
 */
const TouchTooltip = ({
  children,
  ...props
}: Omit<React.ComponentProps<typeof TooltipPrimitive.Root>, 'open' | 'onOpenChange'>) => {
  const [open, setOpen] = React.useState(false)
  const touchedRef = React.useRef(false)

  React.useEffect(() => {
    if (!open) return

    const handleOutsideTouch = () => {
      if (touchedRef.current) {
        setOpen(false)
      }
    }

    document.addEventListener('pointerdown', handleOutsideTouch)
    return () => document.removeEventListener('pointerdown', handleOutsideTouch)
  }, [open])

  const handlePointerDownCapture = React.useCallback((event: React.PointerEvent) => {
    if (event.pointerType === 'touch') {
      event.preventDefault()
      event.stopPropagation()
      touchedRef.current = true
      setOpen((previous) => !previous)
      setTimeout(() => {
        touchedRef.current = false
      }, 300)
    }
  }, [])

  return (
    <TooltipProvider>
      <TooltipPrimitive.Root
        data-slot="tooltip"
        open={open}
        onOpenChange={(nextOpen) => {
          if (!touchedRef.current) setOpen(nextOpen)
        }}
        {...props}
      >
        <span onPointerDownCapture={handlePointerDownCapture} style={{ display: 'contents' }}>
          {children}
        </span>
      </TooltipPrimitive.Root>
    </TooltipProvider>
  )
}

export { Tooltip, TooltipRoot, TouchTooltip, TooltipContent, TooltipProvider, TooltipTrigger }
